'use client';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * @primitive MultiCombobox
 * ROLE: combobox (multi-select) | ARIA: aria-haspopup, aria-expanded, aria-controls, aria-autocomplete=list, aria-activedescendant
 * KEYBOARD: Down/Up nav; Enter commits active; Esc closes; Home/End jump; Backspace on empty query removes the last selected chip
 * FOCUS: input keeps focus across open/close; popover content does not auto-trap
 * PRM: animation kept minimal; reduced-motion respects via CSS
 * FORCED-COLORS: panel border = ButtonText; active option uses Highlight
 * RTL: side/align flips via Radix's logic
 * RSC: CLIENT
 * WAI-APG: https://www.w3.org/WAI/ARIA/apg/patterns/combobox/
 * SUBSTRATE: @radix-ui/react-popover (Trigger asChild on the input)
 *
 * Multi-select combobox. Selected values render as removable chips above the
 * input. Same Radix Popover substrate as single-select `Combobox` so positioning,
 * collision detection, portal stacking, and a11y are identical.
 *
 * Filtering modes:
 *   - default: internal case-insensitive substring filter against `label` /
 *     `keywords` of `options`.
 *   - `noInternalFilter` + `onQueryChange`: parent owns filtering / fetching;
 *     pass already-narrowed `options` on each render and react to `query` events
 *     to drive a server search (see `apps/web/src/components/admin/TypeaheadMultiSelect`
 *     for the async-loader wrapper pattern).
 *
 * The chip row sits above the input. Clicking a chip's X removes that value.
 * Backspace at an empty query removes the last selected value — standard
 * pill-input behavior.
 */
import { useCallback, useEffect, useId, useMemo, useRef, useState, } from 'react';
import * as Popover from '@radix-ui/react-popover';
import { ChevronDown } from 'lucide-react';
import { Pill } from '../../Pill/Pill';
import { Row } from '../../server/layout/Row/index';
import styles from './Combobox.module.css';
function defaultMatch(opt, q) {
    if (q === '')
        return true;
    const lower = q.toLowerCase();
    if (opt.label.toLowerCase().includes(lower))
        return true;
    if (opt.keywords !== undefined && opt.keywords.toLowerCase().includes(lower))
        return true;
    return false;
}
export function MultiCombobox({ options, value, selectedItems, onChange, placeholder = 'Search…', ariaLabel, emptyLabel = 'No matches', loading = false, loadingLabel = 'Searching…', renderOption, renderChip, chipTone = 'neutral', chipSize = 'md', disabled = false, maxPanelHeight = 280, invalid = false, id, onQueryChange, noInternalFilter = false, }) {
    const generatedId = useId();
    const rootId = id ?? generatedId;
    const listboxId = `${rootId}-listbox`;
    const inputRef = useRef(null);
    const listRef = useRef(null);
    const [query, setQuery] = useState('');
    const [open, setOpen] = useState(false);
    const [activeIdx, setActiveIdx] = useState(0);
    // Resolve the chip-rendering set. Caller-provided `selectedItems` wins so
    // async pickers can keep chips for values not present in the current
    // (query-filtered) `options` payload.
    const resolvedSelected = useMemo(() => {
        if (selectedItems !== undefined)
            return selectedItems;
        const byValue = new Map(options.map((o) => [o.value, o]));
        const result = [];
        for (const v of value) {
            const found = byValue.get(v);
            if (found !== undefined)
                result.push(found);
        }
        return result;
    }, [selectedItems, options, value]);
    const selectedSet = useMemo(() => new Set(value), [value]);
    const filtered = useMemo(() => {
        const base = noInternalFilter
            ? options
            : (() => {
                const q = query.trim();
                if (q === '')
                    return options;
                return options.filter((opt) => defaultMatch(opt, q));
            })();
        // Hide already-selected values from the option list — multi-select
        // convention. Re-selecting an existing value is a no-op anyway.
        return base.filter((o) => !selectedSet.has(o.value));
    }, [options, query, noInternalFilter, selectedSet]);
    // Keep activeIdx in range as the filtered list changes.
    useEffect(() => {
        if (activeIdx > filtered.length - 1) {
            setActiveIdx(filtered.length === 0 ? 0 : filtered.length - 1);
        }
    }, [filtered.length, activeIdx]);
    // Scroll the active option into view.
    useEffect(() => {
        if (!open)
            return;
        const node = listRef.current?.querySelector(`[data-cb-idx="${activeIdx}"]`);
        node?.scrollIntoView({ block: 'nearest' });
    }, [open, activeIdx]);
    // Clear the query input when the panel closes.
    useEffect(() => {
        if (!open)
            setQuery('');
    }, [open]);
    const commit = useCallback((opt) => {
        if (selectedSet.has(opt.value))
            return;
        const nextItems = [...resolvedSelected, opt];
        onChange(nextItems.map((i) => i.value), nextItems);
        setQuery('');
        setActiveIdx(0);
        inputRef.current?.focus();
    }, [onChange, resolvedSelected, selectedSet]);
    const remove = useCallback((v) => {
        const nextItems = resolvedSelected.filter((i) => i.value !== v);
        onChange(nextItems.map((i) => i.value), nextItems);
    }, [onChange, resolvedSelected]);
    const onInputChange = (e) => {
        setQuery(e.target.value);
        setOpen(true);
        setActiveIdx(0);
        onQueryChange?.(e.target.value);
    };
    const onKeyDown = (e) => {
        if (disabled)
            return;
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            if (!open) {
                setOpen(true);
                setActiveIdx(0);
                return;
            }
            setActiveIdx((i) => Math.min(i + 1, Math.max(filtered.length - 1, 0)));
        }
        else if (e.key === 'ArrowUp') {
            e.preventDefault();
            if (!open) {
                setOpen(true);
                return;
            }
            setActiveIdx((i) => Math.max(i - 1, 0));
        }
        else if (e.key === 'Home') {
            if (!open)
                return;
            e.preventDefault();
            setActiveIdx(0);
        }
        else if (e.key === 'End') {
            if (!open)
                return;
            e.preventDefault();
            setActiveIdx(Math.max(filtered.length - 1, 0));
        }
        else if (e.key === 'Enter') {
            const opt = filtered[activeIdx];
            if (open && opt !== undefined) {
                e.preventDefault();
                commit(opt);
            }
        }
        else if (e.key === 'Escape') {
            if (open) {
                e.preventDefault();
                setOpen(false);
            }
        }
        else if (e.key === 'Backspace' && query === '' && resolvedSelected.length > 0) {
            // Pill-input convention: empty query + Backspace removes the last chip.
            e.preventDefault();
            const last = resolvedSelected[resolvedSelected.length - 1];
            remove(last.value);
        }
    };
    const activeOptionId = open && filtered[activeIdx]
        ? `${rootId}-opt-${activeIdx}`
        : undefined;
    const inputClass = `${styles.input}${invalid ? ` ${styles['input--invalid']}` : ''}`;
    const showEmpty = !loading && filtered.length === 0;
    return (_jsxs("div", { className: styles.multiRoot, children: [resolvedSelected.length > 0 ? (_jsx(Row, { gap: "sm", wrap: true, children: resolvedSelected.map((item) => renderChip !== undefined ? (_jsx("span", { children: renderChip(item, { onRemove: () => remove(item.value) }) }, item.value)) : (_jsx(Pill, { size: chipSize, tone: chipTone, onRemove: () => remove(item.value), removeAriaLabel: `Remove ${item.label}`, children: item.label }, item.value))) })) : null, _jsx(Popover.Root, { open: open, onOpenChange: setOpen, children: _jsxs("div", { className: styles.root, "data-state": open ? 'open' : 'closed', children: [_jsx(Popover.Trigger, { asChild: true, children: _jsxs("div", { className: styles.inputWrap, children: [_jsx("input", { ref: inputRef, id: rootId, type: "text", role: "combobox", autoComplete: "off", "aria-label": ariaLabel, "aria-autocomplete": "list", "aria-activedescendant": activeOptionId, "aria-expanded": open, "aria-controls": listboxId, "aria-invalid": invalid || undefined, placeholder: placeholder, value: query, onChange: onInputChange, onKeyDown: onKeyDown, onFocus: () => setOpen(true), disabled: disabled, className: inputClass, "data-1p-ignore": "true", "data-lpignore": "true", "data-bwignore": "true" }), _jsx("span", { className: styles.chevron, "aria-hidden": "true", children: _jsx(ChevronDown, {}) })] }) }), _jsx(Popover.Portal, { children: _jsx(Popover.Content, { ref: listRef, role: "listbox", id: listboxId, "aria-label": ariaLabel, className: styles.panel, style: { maxHeight: maxPanelHeight }, side: "bottom", align: "start", sideOffset: 4, collisionPadding: 8, onOpenAutoFocus: (e) => e.preventDefault(), onCloseAutoFocus: (e) => e.preventDefault(), children: loading && filtered.length === 0 ? (_jsx("div", { className: styles.empty, children: loadingLabel })) : showEmpty ? (_jsx("div", { className: styles.empty, children: emptyLabel })) : (filtered.map((opt, idx) => {
                                    const active = idx === activeIdx;
                                    return (_jsx("div", { id: `${rootId}-opt-${idx}`, "data-cb-idx": idx, role: "option", "aria-selected": false, className: `${styles.option} ${active ? styles['option--active'] : ''}`, onMouseEnter: () => setActiveIdx(idx), onMouseDown: (e) => {
                                            e.preventDefault();
                                            commit(opt);
                                        }, children: renderOption ? (renderOption(opt, { active })) : (_jsxs("div", { className: styles.optionBody, children: [_jsx("span", { className: styles.optionLabel, children: opt.label }), opt.sublabel !== undefined ? (_jsx("span", { className: styles.optionSublabel, children: opt.sublabel })) : null] })) }, opt.value));
                                })) }) })] }) })] }));
}
//# sourceMappingURL=MultiCombobox.js.map